import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import {
  getDayNameByDate,
  getTotalHours,
  getTotalLateTime,
  getTotalOvertime,
} from "utils/attendanceUtility";
import GenerateAttendance from "../AttReports/GenerateAttendance";

export default function UpdateMonthlyRecords({
  refreshFunc,
  setUserData,
  selectedMonth,
  setSelectedMonth,
}) {
  const dispatch = useDispatch();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  /*   All States
   ********************************************* */
  // States
  const [loading, setLoading] = useState(false);
  const [monthlyRecords, setMonthlyRecords] = useState([]);
  const [holidayList, setHolidayList] = useState([]);
  // Add state to store all original logs
  const [originalLogs, setOriginalLogs] = useState([]);

  // States from switching to next and previous user
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [usersList, setUsersList] = useState([]);

  const [selectedDevice, setSelectedDevice] = useState(null);

  const loadingOn = () => setLoading(true);
  const loadingOff = () => setLoading(false);

  /*   All Functions
   ********************************************* */
  // Get the number of days in the selected month
  const getDaysInMonth = useCallback(() => {
    const [year, month] = selectedMonth.split("-");
    return new Date(year, month, 0).getDate();
  }, [selectedMonth]);

  // Process logs to show only check-in and check-out for each date
  const processLogsToRecords = useCallback(
    (logs, holidayList) => {
      setOriginalLogs(logs);
      const daysInMonth = getDaysInMonth();
      const [year, month] = selectedMonth.split("-");
      const records = [];
      // Prepare a set of holiday dates for quick lookup
      const holidayDatesSet = new Set(
        holidayList.map((h) => h.date.split("T")[0]),
      );
      for (let day = 1; day <= daysInMonth; day++) {
        const dateString = `${year}-${month}-${String(day).padStart(2, "0")}`;

        // Filter logs for this specific date
        const dayLogs = logs.filter((log) => {
          const logDate = new Date(log.recordTime);
          return logDate.toISOString().slice(0, 10) === dateString;
        });

        let record = {
          date: dateString,
          inTime: "",
          outTime: "",
          inTimeRecordId: null,
          outTimeRecordId: null,
          calculateOverTime: false,
          calculateLateTime: false,
          dayLogs: dayLogs,
          isHoliday: holidayDatesSet.has(dateString),
        };

        if (dayLogs.length > 0) {
          // Sort logs by time
          dayLogs.sort(
            (a, b) => new Date(a.recordTime) - new Date(b.recordTime),
          );

          // First entry is check-in
          if (dayLogs[0]) {
            const inTimeDate = new Date(dayLogs[0].recordTime);
            const inHours = String(inTimeDate.getUTCHours()).padStart(2, "0");
            const inMinutes = String(inTimeDate.getUTCMinutes()).padStart(
              2,
              "0",
            );

            record.inTime = `${inHours}:${inMinutes}`;
            record.inTimeRecordId = dayLogs[0].id;
          }

          // Last entry is check-out
          if (dayLogs.length > 1) {
            const lastLog = dayLogs[dayLogs.length - 1];
            const outTimeDate = new Date(lastLog.recordTime);
            const outHours = String(outTimeDate.getUTCHours()).padStart(2, "0");
            const outMinutes = String(outTimeDate.getUTCMinutes()).padStart(
              2,
              "0",
            );

            record.outTime = `${outHours}:${outMinutes}`;
            record.outTimeRecordId = lastLog.id;
          }

          // Check if any of the logs have calculateOverTime flag
          record.calculateOverTime = dayLogs.some(
            (log) => log.calculateOverTime,
          );

          // Check if any of the logs have calculateLateTime flag
          record.calculateLateTime = dayLogs.some(
            (log) => log.calculateLateTime,
          );
        }

        records.push(record);
      }

      console.log(records);

      setMonthlyRecords(records);
    },
    [getDaysInMonth, selectedMonth, holidayList],
  );

  // Get logs for the selected month - wrapped in useCallback to avoid dependency issues
  const getLogsByMonth = useCallback(async () => {
    try {
      const [year, month] = selectedMonth.split("-");
      const startDate = `${year}-${month}-01`;

      // Last day of month
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${month}-${lastDay}`;

      // Add 1 day to endDate for API call
      const endDateObj = new Date(endDate);
      endDateObj.setDate(endDateObj.getDate() + 1);
      const endDateString = endDateObj.toISOString().slice(0, 10);

      // get year from startDate
      const selectedYear = new Date(startDate).getFullYear();

      let filterBy = searchParams.get("filterBy");

      if (!filterBy) {
        filterBy = "active";
        setSearchParams({ filterBy: "active" });
      }

      loadingOn();
      const { data } = await API.post(
        "/get-user-logs/" + params.userId,
        {
          deviceId: params.deviceId,
          startDate,
          filterBy: filterBy,
          endDate: endDateString,
          year: selectedYear,
        },
        {
          headers: authHeader(),
        },
      );

      if (data?.success) {
        processLogsToRecords(data?.data, data?.holidaysList || []);
        setHolidayList(data?.holidaysList || []);
        setUsersList(data?.usersList || []);
        if (data?.usersList && data?.usersList.length > 0) {
          const currentIndex = data.usersList.findIndex(
            (user) => user.userId === parseInt(params.userId, 10),
          );
          setCurrentUserIndex(currentIndex);
          setSelectedUser(data.usersList[currentIndex]);
        }
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          }),
        );
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  }, [
    selectedMonth,
    params.userId,
    params.deviceId,
    dispatch,
    processLogsToRecords,
  ]);

  // Navigate to the next user in the list
  const goToNextUser = useCallback(() => {
    if (usersList.length === 0 || currentUserIndex >= usersList.length - 1)
      return;

    const nextIndex = currentUserIndex + 1;
    const nextUser = usersList[nextIndex];

    setCurrentUserIndex(nextIndex);
    setSelectedUser(nextUser);

    // Update URL and fetch data for the new user

    // Update params directly for the next API call

    params.userId = nextUser.userId;

    getLogsByMonth();
  }, [usersList, currentUserIndex, params, getLogsByMonth]);

  // Navigate to the previous user in the list
  const goToPrevUser = useCallback(() => {
    if (usersList.length === 0 || currentUserIndex <= 0) return;

    const prevIndex = currentUserIndex - 1;
    const prevUser = usersList[prevIndex];

    setCurrentUserIndex(prevIndex);
    setSelectedUser(prevUser);

    // Update URL and fetch data for the new user

    // Update params directly for the next API call
    params.userId = prevUser.userId;
    getLogsByMonth();
  }, [usersList, currentUserIndex, params, getLogsByMonth]);
  // Handle input changes

  const handleRecordChange = (index, field, value, recordDate) => {
    const updatedRecords = [...monthlyRecords];
    updatedRecords[index][field] = value || "";

    // Auto-set calculateOverTime and calculateLateTime to true when changing inTime or outTime
    if (field === "inTime" || field === "outTime") {
      updatedRecords[index].calculateOverTime = true;
      updatedRecords[index].calculateLateTime = true;
    }

    setMonthlyRecords(updatedRecords);
  };

  // Handle checkbox changes: Update all records for the date
  const handleCheckBoxChange = (field, value, recordDate) => {
    // Update monthlyRecords for display
    const updatedRecords = monthlyRecords.map((record) => {
      if (record.date === recordDate) {
        // Update all logs for this day
        if (record.dayLogs && record.dayLogs.length > 0) {
          record.dayLogs = record.dayLogs.map((log) => ({
            ...log,
            [field]: value,
          }));
        } else {
          record[field] = value;
        }
        // const updatedDayLogs = record?.dayLogs.map((log) => ({
        //   ...log,
        //   [field]: value,
        // }));

        return {
          ...record,
          [field]: value,
          // dayLogs: updatedDayLogs,
        };
      }
      return record;
    });
    setMonthlyRecords(updatedRecords);

    // Update original logs
    const updatedOriginalLogs = originalLogs.map((log) => {
      const logDate = new Date(log.recordTime);
      const logDateString = logDate.toISOString().slice(0, 10);

      if (logDateString === recordDate) {
        return {
          ...log,
          [field]: value,
        };
      }
      return log;
    });
    setOriginalLogs(updatedOriginalLogs);
  };

  const askSaveRecordsConfirmation = () => {
    if (
      window.confirm(
        "Do you want to save " +
          selectedUser?.name +
          "'s data for the whole month for changes you have made?",
      )
    ) {
      saveRecords();
    }
  };

  // Prepare and save all records
  const saveRecords = async () => {
    try {
      // Separate records into update, create, and delete groups
      const updateData = [];
      const createData = [];
      const deleteData = [];

      monthlyRecords.forEach((record) => {
        const [year, month, day] = record.date.split("-");

        // Handle deletion of records when times are cleared
        if (!record.inTime && !record.outTime) {
          // If both times are cleared, delete all existing records for this day
          if (record.dayLogs && record.dayLogs.length > 0) {
            record.dayLogs.forEach((log) => {
              deleteData.push(log.id);
            });
          }
          return;
        }

        // Handle partial deletion - when only one time is cleared
        if (!record.inTime && record.inTimeRecordId) {
          deleteData.push(record.inTimeRecordId);
        }
        if (!record.outTime && record.outTimeRecordId) {
          deleteData.push(record.outTimeRecordId);
        }

        // Update middle logs only (flags). In/out records are handled below
        // with their edited times — pushing them here too sends a stale
        // recordTime that races the real update on the backend.
        if (record.dayLogs && record.dayLogs.length > 0) {
          record.dayLogs.forEach((log) => {
            const isInOrOutRecord =
              log.id === record.inTimeRecordId ||
              log.id === record.outTimeRecordId;

            if (!isInOrOutRecord) {
              updateData.push({
                id: log.id,
                recordTime: log.recordTime,
                // role + date tell the server which entries are a day's in
                // and out. When a day really changed, it keeps exactly those
                // two and excludes the rest -- otherwise a stray earlier
                // punch would still be "earliest" and hide the new in-time.
                role: "middle",
                date: record.date,
                calculateOverTime: record.calculateOverTime,
                calculateLateTime: record.calculateLateTime,
              });
            }
          });
        }

        // Process in time if it exists and no existing record
        if (record.inTime && !record.inTimeRecordId) {
          const [inHours, inMinutes] = record.inTime.split(":");
          const inUtcDate = new Date(
            Date.UTC(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day),
              parseInt(inHours),
              parseInt(inMinutes),
            ),
          );

          createData.push({
            recordTime: inUtcDate.toISOString(),
            deviceId: params.deviceId,
            userId: params.userId,
            role: "in",
            date: record.date,
            calculateOverTime: record.calculateOverTime,
            calculateLateTime: record.calculateLateTime,
          });
        }

        // Process out time if it exists and no existing record
        if (record.outTime && !record.outTimeRecordId) {
          const [outHours, outMinutes] = record.outTime.split(":");
          const outUtcDate = new Date(
            Date.UTC(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day),
              parseInt(outHours),
              parseInt(outMinutes),
            ),
          );

          createData.push({
            recordTime: outUtcDate.toISOString(),
            deviceId: params.deviceId,
            userId: params.userId,
            role: "out",
            date: record.date,
            calculateOverTime: record.calculateOverTime,
            calculateLateTime: record.calculateLateTime,
          });
        }

        // Handle modified times for existing records
        if (record.inTime && record.inTimeRecordId) {
          const [inHours, inMinutes] = record.inTime.split(":");
          const inUtcDate = new Date(
            Date.UTC(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day),
              parseInt(inHours),
              parseInt(inMinutes),
            ),
          );

          updateData.push({
            id: record.inTimeRecordId,
            recordTime: inUtcDate.toISOString(),
            role: "in",
            date: record.date,
            calculateOverTime: record.calculateOverTime,
            calculateLateTime: record.calculateLateTime,
          });
        }

        if (record.outTime && record.outTimeRecordId) {
          const [outHours, outMinutes] = record.outTime.split(":");
          const outUtcDate = new Date(
            Date.UTC(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day),
              parseInt(outHours),
              parseInt(outMinutes),
            ),
          );

          updateData.push({
            id: record.outTimeRecordId,
            recordTime: outUtcDate.toISOString(),
            role: "out",
            date: record.date,
            calculateOverTime: record.calculateOverTime,
            calculateLateTime: record.calculateLateTime,
          });
        }
      });

      loadingOn();
      const { data } = await API.post(
        "/update-monthly-logs",
        { updateData, createData, deleteData },
        { headers: authHeader() },
      );

      if (data?.success) {
        dispatch(
          setNotification({
            message: data?.message || "Records updated successfully",
            type: "success",
          }),
        );
        await getLogsByMonth();
        if (refreshFunc) refreshFunc();
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to update records",
            type: "error",
          }),
        );
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  // Get Device Id
  const GetDeviceById = async (deviceId) => {
    try {
      if (!deviceId) {
        return;
      }
      loadingOn();
      const { data } = await API.get("/get-attendance-device/" + deviceId, {
        headers: authHeader(),
      });
      if (data?.success) {
        setSelectedDevice(data.data);
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const getOvertime = (inTime, outTime, calculateOverTime, date, isHoliday) => {
    if (!calculateOverTime) return "0.0";
    if (!inTime || !outTime) return "0.0";
    if (isHoliday) {
      // On holidays, overtime is just total hours worked
      const totalHoursWorked = getTotalHours(inTime, outTime);
      return totalHoursWorked ? `${totalHoursWorked} hrs (H)` : "0.0";
    }

    if (!selectedUser?.weeklySchedule) {
      return "Weekly schedule not set";
    }
    let weeklySchedule = JSON.parse(selectedUser?.weeklySchedule);
    const dayName = getDayNameByDate(date);
    const isWorkingDay = weeklySchedule[dayName]?.working;
    if (!isWorkingDay) {
      const totalHoursWorked = getTotalHours(inTime, outTime);
      return totalHoursWorked; // No overtime on weekends
    }
    if (!selectedDevice) {
      return "";
    }
    let rules = JSON.parse(selectedUser?.rules || "{}");
    const dayNameOfWeek = getDayNameByDate(date);
    const overTime = getTotalOvertime(
      inTime,
      outTime,
      weeklySchedule[dayNameOfWeek]?.inTime,
      weeklySchedule[dayNameOfWeek]?.outTime,
      selectedUser?.overtimeAfterMinutes,
      rules,
      selectedDevice,
    );
    return overTime ? `${overTime} hrs` : "0.0";
  };

  const getLateTime = (inTime, outTime, date, calculateLateTime) => {
    if (!calculateLateTime) return "0.0";
    if (!inTime || !outTime) return "0.0";

    if (!selectedDevice) {
      return "";
    }

    if (!selectedUser?.weeklySchedule) {
      return "Weekly schedule not set";
    }
    let weeklySchedule = JSON.parse(selectedUser?.weeklySchedule || {});

    let rules = JSON.parse(selectedUser?.rules || "{}");

    const dayNameOfWeek = getDayNameByDate(date);

    const lateTime = getTotalLateTime(
      inTime,
      outTime,
      weeklySchedule[dayNameOfWeek]?.inTime,
      weeklySchedule[dayNameOfWeek]?.outTime,
      selectedUser?.relaxationMinutes,
      rules,
      selectedDevice,
    );
    return lateTime ? `${lateTime} hrs` : "0.0";
  };

  const totalHours = (inTime, outTime) => {
    if (!inTime || !outTime) return "0.0";

    if (!selectedDevice) {
      return "";
    }

    const totalHours = getTotalHours(inTime, outTime);
    return totalHours ? `${totalHours} hrs` : "0.0";
  };

  /*   All UseEffects
   ********************************************* */
  // Get logs when month selection changes
  useEffect(() => {
    let timeout = setTimeout(() => {
      let filterBy = searchParams.get("filterBy");
      if (selectedMonth && filterBy) {
        getLogsByMonth();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedMonth, searchParams]);

  useEffect(() => {
    if (selectedUser) {
      setUserData(selectedUser);
    }
  }, [selectedUser]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (params.deviceId) {
        GetDeviceById(params.deviceId);
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [params]);

  return (
    <div className="px-2 py-2">
      <h3>Update Monthly Records</h3>
      {loading && <Spinner />}

      {/* User Navigation Controls */}
      {usersList.length > 1 && selectedUser && (
        <div className="d-flex align-items-center justify-content-between mb-3 p-2 bg-light rounded">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={goToPrevUser}
            disabled={currentUserIndex <= 0}
          >
            <i className="bi bi-arrow-left"></i> Previous
          </button>

          <div className="text-center">
            <span className="fw-bold">{selectedUser.name || "User"}</span>
            <div className="small text-muted">
              {currentUserIndex + 1} of {usersList.length}
            </div>
          </div>

          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={goToNextUser}
            disabled={currentUserIndex >= usersList.length - 1}
          >
            Next <i className="bi bi-arrow-right"></i>
          </button>
        </div>
      )}

      <div className="mb-3">
        <label htmlFor="monthPicker" className="form-label">
          Select Month
        </label>
        <input
          type="month"
          className="form-control"
          id="monthPicker"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        />
      </div>

      <div
        className="table-responsive "
        style={{
          height: "48vh",
          overflowY: "auto",
        }}
      >
        <table className="table table-striped table-bordered">
          <thead
            className="custom-shadow-sm"
            style={{
              // Sticky
              position: "sticky",
              top: 0,
              background: "#fff",
              zIndex: 1,
            }}
          >
            <tr>
              <th>Date</th>
              <th>Check-In</th>
              <th>Check-Out</th>
              <th>Total Hours</th>
              <th>Late Time</th>
              <th>Calculate Overtime</th>
            </tr>
          </thead>
          <tbody>
            {selectedDevice &&
              monthlyRecords?.map((record, index) => (
                <tr key={record.date}>
                  <td
                    className={`py-1 ${record.isHoliday ? "text-success" : ""}`}
                  >
                    {`  ${record.date} - 
                    ${getDayNameByDate(record.date, 3)}`}
                  </td>
                  <td className="py-1">
                    <input
                      disabled={loading}
                      type="time"
                      className="form-control"
                      value={record.inTime || ""}
                      onChange={(e) =>
                        handleRecordChange(index, "inTime", e.target.value)
                      }
                    />
                  </td>
                  <td className="py-1">
                    <input
                      disabled={loading}
                      type="time"
                      className="form-control"
                      value={record.outTime || ""}
                      onChange={(e) =>
                        handleRecordChange(index, "outTime", e.target.value)
                      }
                    />
                  </td>
                  <td className="py-1">
                    <label htmlFor="lateTime">
                      {totalHours(record.inTime, record.outTime)}
                    </label>
                  </td>
                  <td className="py-1">
                    <div className="form-check">
                      <input
                        disabled={loading}
                        type="checkbox"
                        className="form-check-input"
                        checked={record.calculateLateTime}
                        onChange={(e) =>
                          handleCheckBoxChange(
                            "calculateLateTime",
                            e.target.checked,
                            record.date,
                          )
                        }
                      />
                      <label className="form-check-label">
                        Calculate ({" "}
                        {getLateTime(
                          record.inTime,
                          record.outTime,
                          record.date,
                          record.calculateLateTime,
                        )}
                        )
                      </label>
                    </div>
                  </td>
                  <td className="py-1">
                    <div className="form-check">
                      <input
                        disabled={loading}
                        type="checkbox"
                        className="form-check-input"
                        checked={record.calculateOverTime}
                        onChange={(e) =>
                          handleCheckBoxChange(
                            "calculateOverTime",
                            e.target.checked,
                            record.date,
                          )
                        }
                      />
                      <label className="form-check-label">
                        Calculate ({" "}
                        {getOvertime(
                          record.inTime,
                          record.outTime,
                          record.calculateOverTime,
                          record.date,
                          record.isHoliday,
                        )}{" "}
                        )
                      </label>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="text-end mt-3">
        {selectedUser && (
          <GenerateAttendance
            userData={selectedUser}
            deviceId={selectedUser.deviceId}
          />
        )}
        <button
          className="btn btn-primary ms-1"
          onClick={askSaveRecordsConfirmation}
          disabled={loading}
        >
          Save All Records
        </button>
      </div>
    </div>
  );
}
